import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Tag, Check, Plus, X, Search, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/components/ui/toaster';
import { listTagSetsApi, taskTagsApi } from '@/lib/api';
import { useMyCapabilities } from '@/hooks/use-permissions';
import { cn, getContrastTextColor } from '@/lib/utils';
import type { TagMinimal, EffectiveTag } from '@/types';

type TaskDetailTagsSnapshot =
  | { tags?: { id: string; name: string; color: string; tagSetId: string }[] }
  | undefined;

type TaskDetailRemoveTagsSnapshot = { tags?: { id: string }[] } | undefined;

type TagMutationError = { response?: { data?: { message?: string } } };

interface TagPickerProps {
  /** Required for persisted tasks; omit in draftMode (create task flow). */
  taskId?: string;
  listId: string;
  currentTags?: TagMinimal[];
  disabled?: boolean;
  /** Local-only selection before task exists; toggles do not call the API. */
  draftMode?: boolean;
  onDraftTagsChange?: (tags: TagMinimal[]) => void;
}

export function TagPicker({
  taskId,
  listId,
  currentTags = [],
  disabled,
  draftMode = false,
  onDraftTagsChange,
}: TagPickerProps) {
  const { data: capabilities } = useMyCapabilities();
  const canApply = capabilities?.['task.tags.apply'];
  const canRemove = capabilities?.['task.tags.remove'];
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data: rawAvailableTags, isLoading } = useQuery({
    queryKey: ['effectiveTags', listId],
    queryFn: async () => {
      const response = await listTagSetsApi.getEffectiveTags(listId);
      const data = response.data as EffectiveTag[] | { tags: EffectiveTag[] };
      return Array.isArray(data) ? data : (data?.tags ?? []);
    },
    enabled: open,
  });

  const availableTags = useMemo(
    () => (Array.isArray(rawAvailableTags) ? rawAvailableTags : []),
    [rawAvailableTags]
  );

  const addTagMutation = useMutation({
    mutationFn: (tagId: string) => {
      if (!taskId) throw new Error('taskId required');
      return taskTagsApi.addTag(taskId, tagId);
    },
    onMutate: async (tagId: string) => {
      const tag = availableTags.find((t: EffectiveTag) => t.id === tagId);
      if (!tag || !taskId) return {};
      await queryClient.cancelQueries({ queryKey: ['tasks', 'detail', taskId] });
      const previous = queryClient.getQueryData<TaskDetailTagsSnapshot>(['tasks', 'detail', taskId]);
      queryClient.setQueryData(['tasks', 'detail', taskId], (old: TaskDetailTagsSnapshot) => {
        if (!old) return old;
        const tags = old.tags ?? [];
        if (tags.some((t: { id: string }) => t.id === tagId)) return old;
        return { ...old, tags: [...tags, { id: tag.id, name: tag.name, color: tag.color, tagSetId: tag.tagSetId }] };
      });
      return { previous };
    },
    onSuccess: () => {
      if (taskId) queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
    },
    onError: (
      error: TagMutationError,
      _tagId,
      context: { previous?: TaskDetailTagsSnapshot } | undefined
    ) => {
      if (taskId && context?.previous !== undefined) {
        queryClient.setQueryData(['tasks', 'detail', taskId], context.previous);
      }
      toast({
        title: 'Failed to add tag',
        description: error.response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    },
  });

  const removeTagMutation = useMutation({
    mutationFn: (tagId: string) => {
      if (!taskId) throw new Error('taskId required');
      return taskTagsApi.removeTag(taskId, tagId);
    },
    onMutate: async (tagId: string) => {
      if (!taskId) return {};
      await queryClient.cancelQueries({ queryKey: ['tasks', 'detail', taskId] });
      const previous = queryClient.getQueryData<TaskDetailRemoveTagsSnapshot>(['tasks', 'detail', taskId]);
      queryClient.setQueryData(['tasks', 'detail', taskId], (old: TaskDetailRemoveTagsSnapshot) => {
        if (!old) return old;
        const tags = (old.tags ?? []).filter((t: { id: string }) => t.id !== tagId);
        return { ...old, tags };
      });
      return { previous };
    },
    onSuccess: () => {
      if (taskId) queryClient.invalidateQueries({ queryKey: ['tasks', 'detail', taskId] });
    },
    onError: (
      error: TagMutationError,
      _tagId,
      context: { previous?: TaskDetailRemoveTagsSnapshot } | undefined
    ) => {
      if (taskId && context?.previous !== undefined) {
        queryClient.setQueryData(['tasks', 'detail', taskId], context.previous);
      }
      toast({
        title: 'Failed to remove tag',
        description: error.response?.data?.message || 'Please try again',
        variant: 'destructive',
      });
    },
  });

  const currentTagIds = useMemo(
    () => new Set(currentTags.map((t) => t.id)),
    [currentTags]
  );

  const filteredTags = useMemo(() => {
    const list = availableTags;
    if (!search.trim()) return list;
    const searchLower = search.toLowerCase();
    return list.filter(
      (tag) =>
        tag.name.toLowerCase().includes(searchLower) ||
        (tag.tagSetName && tag.tagSetName.toLowerCase().includes(searchLower))
    );
  }, [availableTags, search]);

  const groupedTags = useMemo(() => {
    const list = Array.isArray(filteredTags) ? filteredTags : [];
    const groups: Record<string, EffectiveTag[]> = {};
    for (const tag of list) {
      if (!groups[tag.tagSetName]) {
        groups[tag.tagSetName] = [];
      }
      groups[tag.tagSetName].push(tag);
    }
    return groups;
  }, [filteredTags]);

  const handleToggleTag = (tag: EffectiveTag) => {
    if (draftMode && onDraftTagsChange) {
      if (currentTagIds.has(tag.id)) {
        onDraftTagsChange(currentTags.filter((t) => t.id !== tag.id));
      } else {
        onDraftTagsChange([
          ...currentTags,
          { id: tag.id, name: tag.name, color: tag.color, tagSetId: tag.tagSetId },
        ]);
      }
      return;
    }
    if (currentTagIds.has(tag.id)) {
      removeTagMutation.mutate(tag.id);
    } else {
      addTagMutation.mutate(tag.id);
    }
  };

  const handleRemoveTag = (e: React.MouseEvent, tagId: string) => {
    e.stopPropagation();
    if (draftMode && onDraftTagsChange) {
      onDraftTagsChange(currentTags.filter((t) => t.id !== tagId));
      return;
    }
    removeTagMutation.mutate(tagId);
  };

  const isPending =
    !draftMode && (addTagMutation.isPending || removeTagMutation.isPending);

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {currentTags.map((tag) => {
        const textColor = getContrastTextColor(tag.color);
        return (
          <Badge
            key={tag.id}
            variant="secondary"
            style={{
              backgroundColor: tag.color || '#6b7280',
              color: textColor,
              borderColor: 'transparent',
            }}
            className="gap-1 pr-1 cursor-default font-medium"
          >
            {tag.name}
            {!disabled && (draftMode ? canApply || canRemove : canRemove) && (
              <button
                onClick={(e) => handleRemoveTag(e, tag.id)}
                className="ml-0.5 rounded-full p-0.5 hover:bg-black/20 transition-colors"
                style={{ color: textColor }}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        );
      })}

      {!disabled && canApply && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 border-[#444] bg-[#2a2a2a] hover:bg-[#333] text-muted-foreground"
            >
              {currentTags.length === 0 ? (
                <>
                  <Tag className="h-3.5 w-3.5" />
                  <span className="text-xs">Add tags</span>
                </>
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5" />
                </>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0" align="start">
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tags..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-8 text-sm"
                />
              </div>
            </div>
            <ScrollArea className="max-h-[300px]">
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : Object.keys(groupedTags).length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {availableTags.length === 0
                    ? 'No tags available for this list'
                    : 'No matching tags'}
                </div>
              ) : (
                <div className="p-1">
                  {Object.entries(groupedTags).map(([setName, tags]) => (
                    <div key={setName}>
                      <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                        {setName}
                      </div>
                      {tags.map((tag) => {
                        const isSelected = currentTagIds.has(tag.id);
                        const textColor = getContrastTextColor(tag.color);
                        return (
                          <button
                            key={tag.id}
                            onClick={() => handleToggleTag(tag)}
                            disabled={isPending}
                            className={cn(
                              'w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md transition-colors',
                              'hover:bg-accent focus:bg-accent focus:outline-none',
                              isPending && 'opacity-50 cursor-not-allowed'
                            )}
                          >
                            <Badge
                              variant="secondary"
                              style={{
                                backgroundColor: tag.color || '#6b7280',
                                color: textColor,
                                borderColor: 'transparent',
                              }}
                              className="font-medium"
                            >
                              {tag.name}
                            </Badge>
                            <div className="flex-1" />
                            {isSelected && (
                              <Check className="h-4 w-4 text-primary" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>
      )}

      {currentTags.length === 0 && disabled && (
        <span className="text-sm text-muted-foreground">No tags</span>
      )}
    </div>
  );
}

interface TagDisplayProps {
  tags?: TagMinimal[];
  maxDisplay?: number;
}

export function TagDisplay({ tags = [], maxDisplay = 3 }: TagDisplayProps) {
  if (tags.length === 0) return null;

  const displayTags = tags.slice(0, maxDisplay);
  const remaining = tags.length - maxDisplay;

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {displayTags.map((tag) => {
        const textColor = getContrastTextColor(tag.color);
        return (
          <Badge
            key={tag.id}
            variant="secondary"
            style={{
              backgroundColor: tag.color || '#6b7280',
              color: textColor,
              borderColor: 'transparent',
            }}
            className="text-xs h-5 px-1.5 font-medium"
          >
            {tag.name}
          </Badge>
        );
      })}
      {remaining > 0 && (
        <span className="text-xs text-muted-foreground">+{remaining}</span>
      )}
    </div>
  );
}
